import { callApi } from './api';
import LiveMeditationSession, { Coach } from '@/models/live-meditation-session';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

const adminApiUrl = import.meta.env.VITE_API_URL_V1 + '/admin/live-sessions';

export const getLiveSessions = async () =>
    callApi<{ sessions: LiveMeditationSession[]; mediaAvailable: boolean }>({ url: adminApiUrl, method: 'GET' });

/**
 * Sessions refresh on an interval because this is a "what is running right now"
 * surface — staff open it precisely when something needs attention, and a stale
 * list (a session shown as live minutes after it ended, or a new one missing) is
 * the one thing it must not be.
 */
export const useLiveSessions = () => useQuery({ queryKey: ['live-sessions'], queryFn: getLiveSessions, refetchInterval: 30000 });

/** Users with the `coach` role — the create form's picker. */
export const getCoaches = async () => callApi<{ coaches: Coach[] }>({ url: `${adminApiUrl}/coaches`, method: 'GET' });

export const useCoaches = () => useQuery({ queryKey: ['live-session-coaches'], queryFn: getCoaches });

/**
 * Only the fields the API's create schema accepts go over the wire — `validate()`
 * rejects unmatched body keys, so an extra key here is a 400, not a silently
 * ignored field.
 */
export type CreateLiveSessionPayload = {
    title: string;
    description?: string;
    meditationType: string;
    durationMinutes: number;
    privacy: string;
    /**
     * The guest list, and for `privacy: 'private'` it is the room's ONLY door —
     * `canJoinLiveSession` reads nothing else and fails closed. Sent only for a
     * private room; the server clears it on a public one.
     */
    accessUserIds?: string[];
    scheduledAt?: string | null;
    coachUserId?: string;
    recurrence?: LiveSessionRecurrencePayload | null;
    settings?: Partial<{
        maxParticipants: number;
        audioPolicy: string;
        videoPolicy: string;
        waitingRoom: boolean;
        participantJoinMuted: boolean;
        participantJoinCameraOff: boolean;
        raiseHandEnabled: boolean;
        reactionsEnabled: boolean;
        chatEnabled: boolean;
    }>;
};

export const createLiveSession = async (payload: CreateLiveSessionPayload) =>
    callApi<{ session: LiveMeditationSession; inviteUrl: string }>({ url: adminApiUrl, method: 'POST', data: payload });

export const useCreateLiveSession = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (payload: CreateLiveSessionPayload) => createLiveSession(payload),
        onSuccess: () => queryClient.invalidateQueries({ queryKey: ['live-sessions'] }),
    });
};

export interface LiveSessionRecurrencePayload {
    frequency: 'daily' | 'weekly';
    timeOfDay: string;
    timezone: string;
    daysOfWeek?: number[];
    active: boolean;
}

export const updateLiveSession = async (
    sessionId: string,
    payload: Partial<CreateLiveSessionPayload> & { recurrence?: LiveSessionRecurrencePayload | null },
) => callApi<{ session: LiveMeditationSession }>({ url: `${adminApiUrl}/${sessionId}`, method: 'PUT', data: payload });

export const useUpdateLiveSession = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({
            sessionId,
            ...payload
        }: { sessionId: string } & Partial<CreateLiveSessionPayload> & { recurrence?: LiveSessionRecurrencePayload | null }) =>
            updateLiveSession(sessionId, payload),
        onSuccess: () => queryClient.invalidateQueries({ queryKey: ['live-sessions'] }),
    });
};

export const removeLiveSession = async (sessionId: string) => callApi<null>({ url: `${adminApiUrl}/${sessionId}`, method: 'DELETE' });

export const useRemoveLiveSession = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (sessionId: string) => removeLiveSession(sessionId),
        onSuccess: () => queryClient.invalidateQueries({ queryKey: ['live-sessions'] }),
    });
};
